import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react'
import { newWedding, planningWedding } from './data'
import type { Role, WeddingData } from './types'

/** Which wedding the prototype shows. Switch it from the "Prototyp" panel. */
export type Scenario = 'new' | 'planning'

type PrototypeContextValue = {
  data: WeddingData
  scenario: Scenario
  role: Role
  /** False for the viewer role: hide or disable every editing action. */
  canEdit: boolean
  setScenario: (scenario: Scenario) => void
  setRole: (role: Role) => void
  /** Apply a mocked change for this session. Reloading or switching scenario resets it. */
  update: (recipe: (data: WeddingData) => WeddingData) => void
}

const PrototypeContext = createContext<PrototypeContextValue | undefined>(undefined)
const STORAGE_KEY = 'slibby-prototype'

function seed(scenario: Scenario) {
  return scenario === 'new' ? newWedding() : planningWedding()
}

function readSaved(): { scenario: Scenario; role: Role } {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}')
    return {
      scenario: saved.scenario === 'new' ? 'new' : 'planning',
      role: saved.role === 'viewer' ? 'viewer' : 'owner',
    }
  } catch {
    return { scenario: 'planning', role: 'owner' }
  }
}

export function PrototypeProvider({ children }: { children: ReactNode }) {
  const [initial] = useState(readSaved)
  const [scenario, setScenarioState] = useState<Scenario>(initial.scenario)
  const [role, setRoleState] = useState<Role>(initial.role)
  const [data, setData] = useState<WeddingData>(() => seed(initial.scenario))

  const persist = useCallback((next: { scenario: Scenario; role: Role }) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
  }, [])

  const setScenario = useCallback(
    (next: Scenario) => {
      setScenarioState(next)
      setData(seed(next))
      persist({ scenario: next, role })
    },
    [persist, role],
  )

  const setRole = useCallback(
    (next: Role) => {
      setRoleState(next)
      persist({ scenario, role: next })
    },
    [persist, scenario],
  )

  const update = useCallback((recipe: (current: WeddingData) => WeddingData) => {
    setData((current) => recipe(current))
  }, [])

  const value = useMemo(
    () => ({ data, scenario, role, canEdit: role !== 'viewer', setScenario, setRole, update }),
    [data, scenario, role, setScenario, setRole, update],
  )

  return <PrototypeContext value={value}>{children}</PrototypeContext>
}

export function usePrototype() {
  const context = useContext(PrototypeContext)
  if (!context) throw new Error('usePrototype must be used within PrototypeProvider')
  return context
}

/** The current mocked wedding. */
export function useWedding() {
  return usePrototype().data
}

export function useCanEdit() {
  return usePrototype().canEdit
}

/** `update` from the store, for mocked changes. */
export function useUpdate() {
  return usePrototype().update
}

/** Replace one item in a list by id. */
export function replaceById<T extends { id: string }>(
  items: readonly T[],
  id: string,
  patch: Partial<T> | ((item: T) => T),
): T[] {
  return items.map((item) =>
    item.id === id ? (typeof patch === 'function' ? patch(item) : { ...item, ...patch }) : item,
  )
}
